import { useCallback, useState, useEffect } from 'react';
import { useFormState } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { getIn } from 'final-form';

import {
  IKnmErknmFormViolationEventMaterial,
  IKnmErknmFormViolationEventMaterialVideoFixation,
} from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { checkHasErrorInCollapseBlock } from 'functions/checkHasErrorInCollapseBlock';
import { IKnmErknmViolationEvidence } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmViolationEvidence';
import { ErknmActViolationEventAttachmentsField } from 'KnmErknmPackage/ErknmViolationsModule/components/ErknmActViolationEventAttachmentsField';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { ITypeDocConfirmationViolationSelectOption } from 'CorePackage/CatalogsModule/interfaces/ITypeDocConfirmationViolationSelectOption';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import {
  createVideoFixationSelectOptions,
  IVideoFixationSelectOption,
} from 'KnmErknmPackage/ErknmViolationsModule/common/createVideoFixationSelectOptions';

import styles from './ErknmViolationItem.module.scss';

interface IErknmActViolationEventMaterialItemProps {
  material: IKnmErknmFormViolationEventMaterial;
  materialIdx: number;
  fieldName: string;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  onRemove: () => void;
  canEdit: boolean;
  isKnm?: boolean;
  isPm?: boolean;
  isDecision?: boolean;
  id: string;
  selectedEventGuid?: string | null;
  actFieldPath?: string;
}

export const ErknmActViolationEventMaterialItem = ({
  material,
  materialIdx,
  fieldName,
  formRef,
  onRemove,
  canEdit,
  isKnm,
  isPm,
  isDecision,
  id,
  selectedEventGuid,
  actFieldPath = 'organization.act',
}: IErknmActViolationEventMaterialItemProps) => {
  const [isKnmCollapseOpened, setIsKnmCollapseOpened] = useState(true);
  const hasError = checkHasErrorInCollapseBlock(formRef, fieldName);
  const typeCode = material.typeDocConfirmationViolation?.code;
  const { values } = useFormState({ subscription: { values: true } });
  const videoFixationsFieldName = `${fieldName}.videoFixations`;
  const remove = formRef?.current.mutators.remove;

  const getVideoFixationOptions = useCallback(
    (currentValue: string | null | undefined) => {
      if (!isDecision) {
        return [];
      }

      const evidences = getIn(values, `${actFieldPath}.violationEvidences`) as IKnmErknmViolationEvidence[] | undefined;
      const allOptions = createVideoFixationSelectOptions(evidences, selectedEventGuid);
      const materialsPath = fieldName.replace(/\[\d+]$/, '');
      const siblingMaterials = getIn(values, materialsPath) as IKnmErknmFormViolationEventMaterial[] | undefined;
      const usedGuids = new Set(
        siblingMaterials
          ?.flatMap(sibling =>
            sibling.videoFixations?.map(item => item.videoFixationOption?.value as string | null | undefined),
          )
          .filter((guid): guid is string => !!guid && guid !== currentValue),
      );

      return allOptions.filter(option => option.value === currentValue || !usedGuids.has(option.value as string));
    },
    [actFieldPath, fieldName, isDecision, selectedEventGuid, values],
  );

  useEffect(() => {
    if (!isDecision || !material.videoFixations?.length) {
      return;
    }

    material.videoFixations.forEach((item, index) => {
      if (!item.videoFixationOption?.value) {
        return;
      }

      const options = getVideoFixationOptions(item.videoFixationOption.value as string);
      const matchedOption = options.find(option => option.value === item.videoFixationOption?.value);
      const currentOption = item.videoFixationOption as IVideoFixationSelectOption | null | undefined;

      if (
        !matchedOption ||
        (matchedOption.label === currentOption?.label && matchedOption.number === currentOption?.number)
      ) {
        return;
      }

      // @ts-ignore
      formRef?.current.change(
        `${videoFixationsFieldName}[${index}].videoFixationOption` as keyof IKnmForm,
        matchedOption,
      );
    });
  }, [formRef, getVideoFixationOptions, isDecision, material.videoFixations, videoFixationsFieldName]);

  const handleTypeChange = useCallback(
    (value: ITypeDocConfirmationViolationSelectOption | null) => {
      if (value?.code === material.typeDocConfirmationViolation?.code) {
        return;
      }

      // @ts-ignore
      formRef?.current.change(`${fieldName}.attachments` as keyof IKnmForm, undefined);
      // @ts-ignore
      formRef?.current.change(`${fieldName}.links` as keyof IKnmForm, undefined);
    },
    [fieldName, formRef, material.typeDocConfirmationViolation?.code],
  );

  if (isDecision) {
    return (
      <FieldArray name={videoFixationsFieldName}>
        {({ fields }) =>
          fields.map((videoFixationFieldName, index) => {
            const item: IKnmErknmFormViolationEventMaterialVideoFixation = fields.value[index];
            const options = getVideoFixationOptions(item?.videoFixationOption?.value as string);

            return (
              <KnmCollapse
                key={item.guid}
                title={`Материал №${index + 1}`}
                bodyClassName={styles.ItemCollapseBody}
                onClose={
                  canEdit && (fields.length || 0) > 1
                    ? () => remove && remove(videoFixationsFieldName, index)
                    : undefined
                }
                openByDefault
                error={false}
              >
                <Row>
                  <FieldBlock
                    id={`${id}.videoFixations[${index}].videoFixationOption`}
                    label={getTitleFieldWithRedStar({
                      label: 'Подтверждающий материал',
                      field: `${videoFixationFieldName}.videoFixationOption`,
                      isDecision,
                    })}
                    placementLabel="left"
                    labelFontSize="small"
                    labelSize="col-3"
                    color="black"
                    className={styles.FieldBlock}
                  >
                    <SelectInputField
                      className="shared-row-field"
                      name={`${videoFixationFieldName}.videoFixationOption`}
                      options={options}
                      isClearable={false}
                      placeholder="Выберите материал"
                      validate={makeRequired('Поле не может быть пустым')}
                      disabled={!canEdit}
                      noOptionsMessage="Значение отсутствует"
                    />
                  </FieldBlock>
                </Row>
              </KnmCollapse>
            );
          })
        }
      </FieldArray>
    );
  }

  return (
    <KnmCollapse
      title={`Материал №${materialIdx + 1}`}
      bodyClassName={styles.ItemCollapseBody}
      onClose={canEdit ? onRemove : undefined}
      openByDefault={material.isNew}
      headerPostfix={material.isNew && <div className={styles.ActStatus}>Черновик</div>}
      changeOpenedCallback={setIsKnmCollapseOpened}
      error={isKnmCollapseOpened && hasError}
    >
      <>
        <Row>
          <FieldBlock
            id={`${id}.typeDocConfirmationViolation`}
            label={getTitleFieldWithRedStar({
              label: 'Тип материала',
              field: `${fieldName}.typeDocConfirmationViolation`,
              isPm,
              isKnm,
              isDecision,
            })}
            placementLabel="left"
            labelFontSize="small"
            labelSize="col-3"
            color="black"
            className={styles.FieldBlock}
          >
            <SelectInputFieldAutoOptions
              className="shared-row-field"
              id={`${fieldName}.typeDocConfirmationViolation`}
              name={`${fieldName}.typeDocConfirmationViolation`}
              optionName={CommonCatalogsNamesEnum.TYPE_DOC_CONFIRMATION_VIOLATION}
              isClearable={false}
              placeholder="Выберите тип материала"
              validate={makeRequired('Поле не может быть пустым')}
              disabled={!canEdit}
              onChange={handleTypeChange}
            />
          </FieldBlock>
        </Row>
        <ErknmActViolationEventAttachmentsField
          formRef={formRef}
          fieldName={fieldName}
          material={material}
          typeCode={typeCode}
          canEdit={canEdit}
          isKnm={isKnm}
          isPm={isPm}
          isDecision={isDecision}
          id={id}
        />
      </>
    </KnmCollapse>
  );
};
